import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import useFormattedTldNavLinkMetadata from '~/builder/utils/useFormattedTldNavLinkMetadata';
import { createDataAutoTagString } from '~/shared/utils/createDataAutoTagString';
import { getBuilderDataAnalyticsProps } from '~/shared/utils/getBuilderDataAnalyticsProps';
import { Link } from '~/techstyle-shared/next-routes';
import { FlagIcon } from '~/techstyle-shared/react-components';

const TLD_NAV_LINK = 'tld_nav_link';

/**
 * Note: The wrapper is defined as an `li` element because the component is originally intended
 * to be used within an `ul` element as per the Builder contract. If you are to use this component
 * outside of a `ul` element, please ensure to update the wrapper element accordingly.
 */
const TldNavLinkWrapper = styled.li`
  list-style: none;

  ${({ theme }) => theme.tldNavLink.wrapper}
`;

const StyledLink = styled.a`
  width: 100%;

  ${({ theme }) => theme.tldNavLink.link}
`;

const CountryFlag = styled(FlagIcon)`
  order: -1;
  margin-right: 16px;
`;

/**
 * Component that renders a TLD (Top-Level Domain) navigation link with copy as defined from an
 * RB in Console and its corresponding country flag. This implementation is used to maintain
 * consistency with the `BuilderCountryDropdown` component and both components are expected to
 * be refactored in the future to manage copy in Builder rather than in Console.
 *
 * The FormattedMessage is packaged together in the `useFormattedTldNavLinkMetadata` hook which was originally
 * implemented for the Builder header models metanav.
 *
 * @param {Object} props - The component props.
 * @param {string} props.tldToNavigateTo - The TLD (locale) used to determine the country label and navigation link.
 * @param {Object} [props.dataAnalytics] - Analytics data for tracking interactions.
 * @param {string} [props.dataAnalytics.category] - The analytics category.
 * @param {string} [props.dataAnalytics.label] - The analytics label.
 * @returns {React.Element} The formatted link message.
 *
 * @example
 * <BuilderTldNavLink
 *   tldToNavigateTo="US"
 *   dataAnalytics={{ category: 'footer', label: 'T1_Footer_United_States' }}
 * />
 */
function BuilderTldNavLink({ tldToNavigateTo, dataAnalytics }) {
  const formattedTldNavLinkMetadata = useFormattedTldNavLinkMetadata();
  const countryByLocale = formattedTldNavLinkMetadata.countries.find(
    (country) => country.code === tldToNavigateTo
  );

  const dataAutotag = dataAnalytics?.label
    ? createDataAutoTagString(dataAnalytics.label)
    : createDataAutoTagString(TLD_NAV_LINK, countryByLocale?.code);

  return (
    <TldNavLinkWrapper
      data-builder-component-type={TLD_NAV_LINK}
      data-autotag={dataAutotag}
      {...getBuilderDataAnalyticsProps(dataAnalytics)}
    >
      <Link href={countryByLocale?.href} passHref>
        <StyledLink>
          {countryByLocale?.label}

          <CountryFlag country={tldToNavigateTo} data-country-menu-icon="" />
        </StyledLink>
      </Link>
    </TldNavLinkWrapper>
  );
}

BuilderTldNavLink.propTypes = {
  dataAnalytics: PropTypes.shape({
    category: PropTypes.string,
    label: PropTypes.string,
  }),
  tldToNavigateTo: PropTypes.string,
};

export default BuilderTldNavLink;
